import { Button } from "../../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../../components/ui/dropdown-menu";
import { Input } from "../../../../../components/ui/input";
import { TableCell, TableRow } from "../../../../../components/ui/table";
import LoggerContext from "../../../../../context/LoggerProvider";
import { config } from "../../../../../Environment";
import useRetryFetch from "../../../../../hooks/useRetryFetch";
import useProcess from "../../../../../hooks/useProcess";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CheckCircle,
  Clock,
  Edit,
  MoreHorizontal,
  XCircle,
} from "lucide-react";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  solid /* regular */,
} from "@fortawesome/fontawesome-svg-core/import.macro";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { toast } from "sonner";
import { Badge } from "../../../../../components/ui/badge";

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "FINISHED":
      return <CheckCircle className="h-3 w-3 text-green-500" />;
    case "TERMINATED":
      return <XCircle className="h-3 w-3 text-red-500" />;
    default:
      return <Clock className="h-3 w-3 text-blue-500" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "FINISHED":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "RUNNING":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case "TERMINATED":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
  }
};

const SingleEngagement = ({ process, refreshLists, setPrescriptions }: any) => {
  const [editingEngagement, setEditingEngagement] = useState<
    | {
        id: string;
        name: string;
      }
    | any
  >({
    id: "",
    name: "",
  });
  const [showTerminate, setShowTerminate] = useState(false);

  const { t } = useTranslation();
  const { Logger } = useContext(LoggerContext);
  const location = useLocation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const { resumeProcess } = useProcess();

  const continueProcess = async (process: any) => {
      let processToContinue = process._id;
      if (process.pendingInnerProcess) {
        Logger.debug("PENDING INNER PROCESS detected, switching to this one!");
        processToContinue = processToContinue.pendingInnerProcess;
      }
      await resumeProcess({
        processId: processToContinue,
        from: location,
        forward: location,
        onError: () => {
          toast.error("An error occurred while continuing the process. Please try again.");
          refreshLists();
        },
      });
  };

  const handleUpdateProcessName = async (
    processId: string,
    newName: string
  ) => {
    if (newName.trim() === "") return;

    try {
      const updateResponse = await retryFetch(
        `${config.API_BASE}/api/process/${processId}/engagement-name`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            engagementName: newName,
          }),
        }
      );

      const data = await updateResponse.json();

      if (!updateResponse.ok) {
        throw new Error(data?.error || "Failed to update process name");
      }

      setPrescriptions((prevPrescriptions: any) =>
        prevPrescriptions.map((prescription: any) => {
          if (prescription._id === data.process.fiPrescription) {
            return {
              ...prescription,
              processes: prescription.processes.map((process: any) =>
                process._id === processId
                  ? { ...process, engagementName: newName }
                  : process
              ),
            };
          }
          return prescription;
        })
      );
    } catch (error) {
      console.error("Failed to update process name:", error);
    } finally {
      setEditingEngagement(null);
    }
  };

  const showUserLog = (process: any) => {
    Logger.debug("UserLog should be shown for " + process._id);
    let stateToPass = {
      processId: process._id,
      presName: process.presName,
      presHistoryId: process.presHistoryId,
      expertEmail: process.expertEmail,
      autochat: false,
    };
    navigate("/review", { state: stateToPass });
  };

  return (
    <>
      <TableRow className="h-10">
        <TableCell className="font-medium min-w-[200px] w-[90%] md:w-[40%] py-0 border-none">
          {editingEngagement?.id === process._id ? (
            <div className="flex items-center gap-2">
              <Input
                value={editingEngagement.name}
                onChange={(e) =>
                  setEditingEngagement({
                    ...editingEngagement,
                    name: e.target.value,
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleUpdateProcessName(
                      process._id,
                      editingEngagement.name
                    );
                  } else if (e.key === "Escape") {
                    setEditingEngagement(null);
                  }
                }}
                className="h-8 text-sm"
                autoFocus
              />
              <Button
                size="sm"
                className="h-8 px-2 py-0"
                onClick={() =>
                  handleUpdateProcessName(process._id, editingEngagement.name)
                }
              >
                {t("save")}
              </Button>
            </div>
          ) : (
            <div className="flex items-center max-w-full">
              <span className="md:hidden pr-2">
                {getStatusIcon(process.status)}
              </span>
              <div className="flex-1 truncate whitespace-nowrap overflow-hidden max-w-60">
                {process.engagementName}
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 flex-shrink-0"
                onClick={() =>
                  setEditingEngagement({
                    id: process._id,
                    name: process.engagementName,
                  })
                }
              >
                <Edit className="h-3 w-3" />
              </Button>
            </div>
          )}
        </TableCell>
        <TableCell className="min-w-[120px] w-[20%] hidden md:table-cell py-0 border-none ">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`text-xs flex items-center space-x-1 ${getStatusColor(
                process.status
              )}`}
            >
              <span>{getStatusIcon(process.status)}</span>
              <span className="leading-4 mt-[1px]">{t(process.status)}</span>
            </Badge>
          </div>
        </TableCell>
        <TableCell className="min-w-[150px] w-[25%] whitespace-nowrap hidden md:table-cell py-0 border-none">
          {formatDate(process.lastUpdated)}
        </TableCell>
        <TableCell className="text-right align-middle px-1 w-auto md:w-0 md:px-2 py-0 border-none">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 p-0 w-8 ml-0 md:ml-auto md:pr-3"
                aria-label="Actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {process.status === "RUNNING" ? (
                <>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => continueProcess(process)}
                  >
                    <div className="flex">
                      <FontAwesomeIcon
                        title={t("continue")}
                        icon={solid("forward")}
                        className="ml-2 mr-2 mt-1 w-[15px]"
                      />
                      <span>{t("resume")}</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => setShowTerminate(true)}
                  >
                    <div className="flex">
                      <FontAwesomeIcon
                        title={t("terminate")}
                        icon={solid("circle-xmark")}
                        className="ml-2 mr-2 mt-1 w-[15px]"
                      />
                      <span>{t("terminate")}</span>
                    </div>
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => showUserLog(process)}
                >
                  <div className="flex">
                    <FontAwesomeIcon
                      title={t("review")}
                      icon={solid("file-lines")}
                      className="ml-2 mr-2 mt-1 w-[15px]"
                    />
                    <span>{t("review")}</span>
                  </div>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
      <Dialog open={showTerminate} onOpenChange={setShowTerminate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("terminate_engagement")}</DialogTitle>
          </DialogHeader>
          <p>{t("question_terminate")}</p>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={async () => {
                try {
                  Logger.debug("Current engagement: " + process._id);

                  const response = await retryFetch(
                    `${config.API_BASE}/api/processes/${process._id}`,
                    {
                      method: "DELETE",
                      credentials: "include",
                    }
                  );

                  if (!response.ok) {
                    throw new Error("Failed to delete the process.");
                  }

                  const msg = await response.json();
                  toast.success(t(msg.message));

                  setShowTerminate(false);
                  setPrescriptions((prevPrescriptions: any) =>
                    prevPrescriptions.map((prescription: any) => {
                      if (prescription._id === process.fiPrescription) {
                        return {
                          ...prescription,
                          processes: prescription.processes.map((proc: any) =>
                            proc._id === process._id
                              ? { ...proc, status: "TERMINATED" }
                              : proc
                          ),
                        };
                      }
                      return prescription;
                    })
                  );
                } catch (error) {
                  console.error("Error terminating process:", error);
                  toast.error(
                    "An error occurred while terminating the process."
                  );
                }
              }}
              data-id={"yes"}
              key={0}
              variant={`default`}
            >
              {t("yes")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowTerminate(false);
              }}
              data-id={"no"}
              key={1}
              variant={`outline`}
            >
              {t("no")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SingleEngagement;
